// Stateful shell hook for the Career CLI shell pane.
//
// Owns: cwd, command history (persisted to localStorage, capped at 200 entries),
// the visible output buffer, and the current input line. Only history is
// persisted — cwd, output, and input are intentionally ephemeral.
//
// Dispatches commands through shellCommands.ts. Side-effect actions
// (open-rubik, open-resume-pdf, go-classic) are bubbled up via onAction
// so TmuxLayout's existing handler runs them.
//
// Unknown-command fallback: instead of `bash: foo: command not found`, the
// shell hands the input to Gemini with a witty system prompt and streams
// the response back into the output buffer. Input is blocked during the
// stream so concurrent execs can't interleave with the in-flight tokens.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  COMMANDS,
  complete,
  tokenize,
  type ShellAction,
  type ShellContext,
} from './shellCommands';
import { INITIAL_CWD, prettyPath } from './filesystem';
import { streamChat } from './chat/geminiClient';

// ---------------------------------------------------------------------------
// History persistence
// ---------------------------------------------------------------------------

const HISTORY_STORAGE_KEY = 'careercli.shell.history';
const HISTORY_MAX_ENTRIES = 200;

function loadHistoryFromStorage(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(HISTORY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Defensive: only keep string entries, and enforce cap in case the stored
    // value was written by an older build with a different limit.
    return parsed.filter((x): x is string => typeof x === 'string').slice(-HISTORY_MAX_ENTRIES);
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Gemini fallback prompt
// ---------------------------------------------------------------------------

const SHELL_SYSTEM_PROMPT = `You are a witty, irreverent terminal assistant running inside the shell
pane of Thanh Nguyen Tan Thanh's portfolio site. The visitor typed
something that is NOT a recognised shell command, so it fell through to
you.

About Thanh (only mention if relevant):
- Senior QC Engineer at MODEC (Mar 2026 → present).
- 11+ years in test automation, networking, API testing, CI/CD.
- Strengths: Python, Selenium, Playwright, Postman, Jenkins.
- Vietnam-based, formerly Cisco (on-site San Jose), Terralogic, TMA.

Rules:
- Format your reply as plain-text terminal output. No markdown headings,
  no bullet lists, no code fences, no emojis.
- KEEP IT SHORT: 1–3 sentences, hard cap 4. Visitors are reading a
  shell, not an essay.
- Lean witty / dry / QA-meme over earnest. Drop dev/QA/devops references
  when they land (pytest, kubectl, Jenkins, vim, Friday deploy, flaky
  tests, OWASP, etc.).
- When the question is factual about Thanh, answer it, then suggest a
  real shell command they could try: \`cat about/bio.md\`, \`tree\`,
  \`ls experience\`, \`open log\`, etc.
- For nonsense or chatty inputs, riff in-character as a sarcastic
  terminal that knows it's a portfolio site.
- Never break character to apologise about not being a real bash.
- Never invent fake credentials, employers, or quotes attributed to
  real people. When in doubt, attribute to "internal Slack".`;

// ---------------------------------------------------------------------------
// Output buffer
// ---------------------------------------------------------------------------

export type OutputKind = 'echo' | 'stdout' | 'stderr' | 'gemini';

export interface OutputEntry {
  id: number;
  kind: OutputKind;
  /** Pretty-path snapshot of cwd at the time `echo` was pushed. */
  cwd?: string;
  text: string;
  /** True while Gemini tokens are still being appended into `text`. The UI
   *  renders a blinking caret on streaming entries. */
  streaming?: boolean;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseShellOptions {
  onAction?: (action: ShellAction) => void;
}

export interface UseShellResult {
  cwd: string;
  prettyCwd: string;
  input: string;
  setInput: (next: string) => void;
  output: OutputEntry[];
  exec: (line: string) => void;
  tab: () => void;
  recallPrev: () => void;
  recallNext: () => void;
  isStreaming: boolean;
}

export function useShell(options: UseShellOptions = {}): UseShellResult {
  const { onAction } = options;

  const [cwd, setCwd] = useState<string>(INITIAL_CWD);
  const [input, setInput] = useState<string>('');
  const [output, setOutput] = useState<OutputEntry[]>([]);
  const [history, setHistory] = useState<string[]>(loadHistoryFromStorage);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);

  // Persist history on every change. Quota-exceeded or serialization errors
  // shouldn't crash the app — silently drop the write.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
    } catch (err) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[useShell] failed to persist history:', err);
      }
    }
  }, [history]);

  // History recall cursor — -1 means "live input", 0..n indexes history newest→oldest.
  const recallCursorRef = useRef<number>(-1);
  // Backup of the user's in-progress input while they walk through history.
  const liveInputRef = useRef<string>('');
  const idCounterRef = useRef<number>(0);
  // Mirrors `isStreaming` for the async exec path — avoids racing two streams
  // if the input box briefly accepts a key during state transitions.
  const streamingRef = useRef<boolean>(false);

  const nextId = () => ++idCounterRef.current;

  const appendOutput = useCallback((entries: OutputEntry[]) => {
    setOutput((prev) => [...prev, ...entries]);
  }, []);

  const ctxRef = useRef<ShellContext>({
    cwd,
    setCwd,
    history,
  });
  // Keep the dispatcher's view of state fresh — commands run synchronously,
  // so we just rewrite the ref before each exec.
  ctxRef.current = { cwd, setCwd, history };

  const exec = useCallback(
    async (raw: string) => {
      // Guard re-entry while Gemini is still streaming a previous reply.
      if (streamingRef.current) return;

      const line = raw.replace(/\r/g, '');
      const trimmed = line.trim();
      const entries: OutputEntry[] = [];

      // Always echo the user's input, including blank lines, so the prompt
      // visibly "scrolls" up. Empty line: echo and stop.
      entries.push({
        id: nextId(),
        kind: 'echo',
        cwd: prettyPath(cwd),
        text: line,
      });
      if (!trimmed) {
        appendOutput(entries);
        return;
      }

      // History: dedupe with previous entry; ignore blank lines. Cap at
      // HISTORY_MAX_ENTRIES — drop oldest entries when exceeding.
      setHistory((prev) =>
        prev[prev.length - 1] === trimmed
          ? prev
          : [...prev, trimmed].slice(-HISTORY_MAX_ENTRIES),
      );

      const tokens = tokenize(trimmed);
      // Visitors arriving from the chat panes are used to slash-prefixed
      // commands (`/joke`, `/timeline`, …). Strip a leading slash here so
      // `/rubik` in the shell does the same thing as `rubik`.
      const rawName = tokens[0];
      const name = rawName.startsWith('/') ? rawName.slice(1) : rawName;
      const args = tokens.slice(1);
      const cmd = COMMANDS[name];

      if (!cmd) {
        // No matching command → hand off to Gemini as a witty fallback.
        // Push the echo + an empty streaming entry, then append tokens as
        // they arrive. Input is blocked for the duration via isStreaming.
        const streamId = nextId();
        appendOutput([
          ...entries,
          { id: streamId, kind: 'gemini', text: '', streaming: true },
        ]);
        streamingRef.current = true;
        setIsStreaming(true);
        try {
          let acc = '';
          for await (const delta of streamChat(SHELL_SYSTEM_PROMPT, [], trimmed)) {
            acc += delta;
            // Mutate just the streaming entry, leave the rest alone.
            setOutput((prev) =>
              prev.map((e) => (e.id === streamId ? { ...e, text: acc } : e)),
            );
          }
          // Strip trailing whitespace so a blank tail line doesn't push the
          // next prompt down unnecessarily.
          const finalText = acc.replace(/\s+$/, '');
          setOutput((prev) =>
            prev.map((e) =>
              e.id === streamId ? { ...e, text: finalText, streaming: false } : e,
            ),
          );
        } finally {
          streamingRef.current = false;
          setIsStreaming(false);
        }
        return;
      }

      const result = cmd.handler(args, ctxRef.current);

      if (result.clear) {
        // `clear` wipes the buffer including the echo line we just pushed.
        setOutput([]);
        if (result.stdout) {
          appendOutput([{ id: nextId(), kind: 'stdout', text: result.stdout }]);
        }
        if (result.stderr) {
          appendOutput([{ id: nextId(), kind: 'stderr', text: result.stderr }]);
        }
      } else {
        if (result.stdout) {
          entries.push({ id: nextId(), kind: 'stdout', text: result.stdout });
        }
        if (result.stderr) {
          entries.push({ id: nextId(), kind: 'stderr', text: result.stderr });
        }
        appendOutput(entries);
      }

      if (result.action && onAction) onAction(result.action);
    },
    [cwd, appendOutput, onAction],
  );

  const tab = useCallback(() => {
    const res = complete(input, cwd);
    if (res.newInput !== input) setInput(res.newInput);
    if (res.suggestions && res.suggestions.length > 1) {
      // Echo the current prompt so the listing reads like a real tty, then
      // dump the suggestions as stdout. We don't push to history.
      setOutput((prev) => [
        ...prev,
        { id: nextId(), kind: 'echo', cwd: prettyPath(cwd), text: input },
        { id: nextId(), kind: 'stdout', text: res.suggestions!.join('  ') },
      ]);
    }
    recallCursorRef.current = -1;
  }, [input, cwd]);

  // History recall — newest at the bottom of `history`, so Up walks toward
  // index 0 (oldest) starting from the most-recent entry.
  const recallPrev = useCallback(() => {
    if (history.length === 0) return;
    if (recallCursorRef.current === -1) {
      liveInputRef.current = input;
      recallCursorRef.current = history.length - 1;
    } else if (recallCursorRef.current > 0) {
      recallCursorRef.current--;
    }
    setInput(history[recallCursorRef.current]);
  }, [history, input]);

  const recallNext = useCallback(() => {
    if (recallCursorRef.current === -1) return;
    if (recallCursorRef.current < history.length - 1) {
      recallCursorRef.current++;
      setInput(history[recallCursorRef.current]);
    } else {
      recallCursorRef.current = -1;
      setInput(liveInputRef.current);
    }
  }, [history]);

  const setInputWrapper = useCallback((next: string) => {
    setInput(next);
    recallCursorRef.current = -1;
  }, []);

  return {
    cwd,
    prettyCwd: prettyPath(cwd),
    input,
    setInput: setInputWrapper,
    output,
    exec,
    tab,
    recallPrev,
    recallNext,
    isStreaming,
  };
}
